import {
  flow,
  SnapshotIn,
} from 'mobx-state-tree';
import moment from 'moment-timezone';
import Hades from '@services/Hades';
import { IBenefitVendors } from '@stores/ApplicationInterfaces';
import BenefitVendor from '@stores/BenefitVendor';
import { BenefitTypes, trackBenefitEntityCreated, trackBenefitEntityDeleted, trackBenefitEntityEdited } from '@utils/analytics';

export default {
  actions: (self: { hades: Hades; benefitVendors: IBenefitVendors }) => ({
    getBenefitVendorsList: flow(function* getBenefitVendorsList(
      organizationId,
    ) {
      const result: SnapshotIn<typeof BenefitVendor>[] = yield self.hades.request(
        'benefit.vendorList',
        {
          organizationId,
          allRecords: true,
        },
      );

      self.benefitVendors.clear();

      result.forEach((vendor: any) => {
        self.benefitVendors.set(vendor.id, {
          ...vendor,
          // set organizationIds to make mobx resolving linked organization entities from the tree
          organizations: vendor.organizations.map((org: any) => org.id),
          criteria: vendor.criteria && typeof vendor.criteria !== 'string'
          ? JSON.stringify(vendor.criteria)
          : vendor.criteria,
        });
      });
    }),

    createBenefitVendor: flow(function* createBenefitVendor(
      {
        name,
        criteria,
        description,
        eligibilityInfo,
        hoursOfOperation,
        organizations,
        logo,
        logoUrl,
        activeFrom,
        activeTo,
      }
    ) {
      const organizationIds = organizations.map((org: any) => org.id);
      const vendorData = {
        name,
        criteria,
        description,
        eligibilityInfo,
        hoursOfOperation,
        organizationIds,
        ...(logo && {logo}),
        ...(logoUrl && {logoUrl}),
        activeFrom: moment(activeFrom).utc(),
        activeTo: moment(activeTo).utc(),
      };

      const result: SnapshotIn<typeof BenefitVendor> = yield self.hades.request(
        'benefit.vendorCreate',
        vendorData,
      );

      if (result) {
        self.benefitVendors.set(`${result.id}`, { ...result, organizations: organizationIds });
      }

      trackBenefitEntityCreated(BenefitTypes.Vendor, { ...vendorData, logo: result.logoUrl });

      return result;
    }),

    updateBenefitVendor: flow(function* updateBenefitVendor(
      {
        id: vendorId,
        name,
        criteria,
        description,
        eligibilityInfo,
        hoursOfOperation,
        organizations,
        logo,
        logoUrl,
        activeFrom,
        activeTo,
      }
    ) {
      const organizationIds = organizations.map((org: any) => org.id);
      const vendorData = {
        vendorId,
        name,
        criteria,
        description,
        eligibilityInfo,
        hoursOfOperation,
        organizationIds,
        ...(logo && {logo}),
        ...(!logo && logoUrl && {logoUrl}),
        activeFrom: moment(activeFrom).utc(),
        activeTo: moment(activeTo).utc(),
      };

      const result: SnapshotIn<typeof BenefitVendor> = yield self.hades.request(
        'benefit.vendorUpdate',
        vendorData,
      );

      if (result) {
        self.benefitVendors.set(`${result.id}`, { ...result, organizations: organizationIds });
      }

      trackBenefitEntityEdited(BenefitTypes.Vendor, { ...vendorData, logo: result.logoUrl, organizations: organizationIds });

      return { ...result, organizations: organizationIds };
    }),

    deleteBenefitVendor: flow(function* deleteBenefitVendor(
      {
        id: vendorId,
        organizationId,
      }
    ) {
      const vendorData = {
        vendorId,
        organizationId,
      };

      const result: SnapshotIn<typeof BenefitVendor> = yield self.hades.request(
        'benefit.vendorDelete',
        vendorData,
      );

      if (result) {
        self.benefitVendors.delete(`${vendorId}`);
      }

      trackBenefitEntityDeleted(BenefitTypes.Vendor, vendorData);

      return result;
    }),
  }),
};
